/**
 * MobileTabBar — fixed bottom tab navigation for mobile (and standalone PWA mode).
 * Shows: Traditions · Oracle · Weekly · Messages · You.
 * App notifications (follows, replies, shares, etc.) live behind the bell icon
 * in the top nav. This tab bar leads to the DM inbox.
 */
import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BookOpen, Sparkles, Sunrise, MessageSquare, UserCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const tabs = [
  { to: "/traditions", label: "Traditions", Icon: BookOpen, testid: "tab-traditions" },
  { to: "/oracle",  label: "Oracle",  Icon: Sparkles, testid: "tab-oracle" },
  { to: "/weekly",  label: "Weekly",  Icon: Sunrise,  testid: "tab-weekly" },
  { to: "/messages", label: "Messages", Icon: MessageSquare, testid: "tab-messages" },
  { to: "/settings#you",label: "You",     Icon: UserCircle2, testid: "tab-you" },
];

export default function MobileTabBar() {
  const { user } = useAuth();
  const location = useLocation();
  const [dmUnread, setDmUnread] = useState(0);

  // Hide the bar on /login (no destination yet) and on the Landing page.
  const hide = location.pathname === "/login" || location.pathname === "/";

  useEffect(() => {
    if (!user) { setDmUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!cancelled) setDmUnread(data?.unread || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    // Poll every 60s — cheap and good enough for mobile.
    const t = setInterval(fetchUnread, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname]);

  if (hide) return null;

  // All five tabs are always visible — even when logged out — so the bottom bar
  // never reshuffles based on auth state. Messages + You guard themselves with
  // an inline login prompt instead of redirecting.
  const visible = tabs;

  return (
    <nav
      data-testid="mobile-tab-bar"
      aria-label="Primary"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border safe-bottom will-change-transform"
      style={{ transform: "translateZ(0)" }}
    >
      <ul className="grid grid-cols-5 max-w-md mx-auto">
        {visible.map(({ to, label, Icon, testid }) => (
          <li key={to}>
            <NavLink
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 py-2.5 px-1 min-h-[56px] transition-colors active:scale-[0.96] ${
                  isActive
                    ? "text-[hsl(var(--primary))]"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={20} strokeWidth={isActive ? 2 : 1.6} />
                  <span className="mono text-[9px] uppercase tracking-[0.16em] leading-none">{label}</span>
                  {to === "/messages" && dmUnread > 0 && (
                    <span
                      data-testid="tab-messages-dot"
                      aria-label={`${dmUnread} unread message${dmUnread === 1 ? "" : "s"}`}
                      className="absolute top-1.5 right-[24%] min-w-[16px] h-4 px-1 inline-flex items-center justify-center mono text-[9px] tabular-nums bg-[hsl(var(--primary))] text-background rounded-full"
                    >
                      {dmUnread > 99 ? "99+" : dmUnread}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
